import React, { useContext, useEffect, useState } from 'react';
import { AuthenticationContext } from './AuthenticationProvider';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { alphaSortObjectArray, b64toBlob, getTranslation } from '../utility';
import { Project } from './types/Project';
import {
  getProjectIndex,
  createProject,
  updateProject,
  deleteProject,
  saveProjectImage,
} from './api';
import { ProjectsResponse } from './types/ProjectsResponse';
import { trackPromise } from 'react-promise-tracker';

export interface IProjectData {
  projectId: number;
  projectName: string | undefined;
  projectDescription: string | undefined;
  projectImage: string | undefined;
  enProjectName: string | undefined;
  frProjectName: string | undefined;
  enProjectDescription: string | undefined;
  frProjectDescription: string | undefined;
}

export type AddProject = {
  id?: number;
  en_project_name: string | undefined;
  fr_project_name: string | undefined;
  en_project_description: string | undefined;
  fr_project_description: string | undefined;
};

export interface IHomeContext {
  projects: IProjectData[];

  onGotoProject(_projectId: number): void;
  onCreateProject(_values: AddProject): void;
  onUpdateProject(_values: AddProject): void;
  onDeleteProject(_projectId: number): void;
  onSaveProjectImage(_projectId: number, _imageURI: string): void;
}

export interface IHomeProviderProps {
  children: any;
}

const HomeContext = React.createContext<IHomeContext>({
  projects: [],

  onGotoProject(_projectId: number): void {},
  onCreateProject(_values: AddProject): void {},
  onUpdateProject(_values: AddProject): void {},
  onDeleteProject(_projectId: number): void {},
  onSaveProjectImage(_projectId: number, _imageURI: string): void {},
});

const createDataArray = (data: ProjectsResponse, language: 'en' | 'fr') => {
  let arr: IProjectData[] = [];
  if (data?.projects) {
    data.projects.forEach((project) => {
      arr.push({
        projectId: project.id as number,
        projectName: getTranslation(language, project.name),
        projectDescription: getTranslation(language, project.description),
        projectImage: project.image_url,
        enProjectName: project.name.en?.translation,
        enProjectDescription: project.description.en?.translation,
        frProjectName: project.name.fr?.translation,
        frProjectDescription: project.description.fr?.translation,
      });
    });
  }

  alphaSortObjectArray(arr, 'projectName');
  return arr;
};

const HomeProvider = (props: IHomeProviderProps) => {
  const { children } = props;

  const [projects, setProjects] = useState<IProjectData[]>([]);
  const { i18n } = useTranslation();

  const { isAuthenticated, token } = useContext(AuthenticationContext);

  const navigate = useNavigate();

  const language = i18n.language as 'en' | 'fr';

  useEffect(() => {
    if (isAuthenticated() && token) {
      trackPromise(getProjectIndex(token)).then((data) => {
        if (data) {
          const arr = createDataArray(data as ProjectsResponse, language);
          setProjects(arr);
        }
      });
    }
  }, [isAuthenticated, i18n, token, language]);

  const onGotoProject = (projectId: number) => {
    navigate(`/project/${projectId}/overview`);
  };

  const onCreateProject = (values: AddProject) => {
    if (isAuthenticated() && token) {
      const create: Project = {
        remote_repository_url: undefined,
        name: {
          en: { translation: values.en_project_name || '' },
          fr: { translation: values.fr_project_name || '' },
        },
        description: {
          en: { translation: values.en_project_description || '' },
          fr: { translation: values.fr_project_description || '' },
        },
      };

      trackPromise(createProject(create, token)).then((_response) => {
        trackPromise(getProjectIndex(token)).then((data) => {
          if (data) {
            const arr = createDataArray(data as ProjectsResponse, language);
            setProjects(arr);
          }
        });
      });
    }
  };

  const onUpdateProject = (values: AddProject) => {
    if (isAuthenticated() && token) {
      const update: Project = {
        id: values.id,
        remote_repository_url: undefined,
        name: {
          en: { translation: values.en_project_name || '' },
          fr: { translation: values.fr_project_name || '' },
        },
        description: {
          en: { translation: values.en_project_description || '' },
          fr: { translation: values.fr_project_description || '' },
        },
      };

      trackPromise(updateProject(update, token)).then((_response) => {
        trackPromise(getProjectIndex(token)).then((data) => {
          const arr = createDataArray(data as ProjectsResponse, language);
          setProjects(arr);
        });
      });
    }
  };

  const onDeleteProject = (projectId: number) => {
    if (isAuthenticated() && token) {
      deleteProject(projectId, token).then((_response) => {
        getProjectIndex(token).then((data) => {
          const arr = createDataArray(data as ProjectsResponse, language);
          setProjects(arr);
        });
      });
    }
  };

  const onSaveProjectImage = (projectId: number, imageURI: string) => {
    if (isAuthenticated() && token) {
      const blob = b64toBlob(imageURI);

      let formData = new FormData();
      formData.append('image', blob);

      trackPromise(saveProjectImage(projectId, formData, token)).then(() =>
        trackPromise(getProjectIndex(token)).then((data) => {
          if (data) {
            const arr = createDataArray(data as ProjectsResponse, language);
            setProjects(arr);
          }
        })
      );
    }
  };

  return (
    <HomeContext.Provider
      value={{
        projects,
        onGotoProject,
        onCreateProject,
        onUpdateProject,
        onDeleteProject,
        onSaveProjectImage,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
};

export { HomeContext, HomeProvider };
